import type { LoggingArgs } from "@1js/office-online-telemetry";
import {
  TraceLevel,
  formatForUpload,
  uploadLogs,
} from "@1js/office-online-telemetry";
import type { LoggerConfig } from "./loggerConfig";
import {
  getRelativeTimeSinceNavigationStart,
  getAbsoluteTimeSinceEpoch,
  getBuildNumber,
} from "../utils";

const messageMaxChars: number = 3072; // 3KB
const trimmedMessage: string = "... [Trimmed]";
const fastUploadCadenceInMs: number = 1000;

// TODO 3846361: move AdaptiveLogger to office-online-telemetry package
export class bUlsCache {
  /** Cadence between each upload. */
  private _uploadCadenceInMs = 30 * 1000;

  /** Number of logs in cache which will trigger immediate upload. */
  private _uploadTriggerSize = 30;

  /** Rolling counter used to identify set of logs being uploaded to the server. */
  private _uploadId = 0;

  /** Actually cache collection */
  private _logCache: LoggingArgs[] = [];

  /** We use setTimeout to periodically upload logs, that's the Id of the last timeout */
  private _timeoutId: number | undefined;

  /** Base timestamp for all the logs in current batch */
  private _timestampBase = getAbsoluteTimeSinceEpoch();

  /** Callbacks to run when an upload batch is complete */
  private _onCompleteUploadCallbacks: (() => void)[] = [];

  private _userSessionId: string = "";
  private _remoteUrlEndpoint: string | undefined;

  private _timeSpentLogging = 0;

  public constructor(config?: LoggerConfig) {
    if (config) {
      this.configure(config);
    }
  }

  public getTimeSpentLogging() {
    return this._timeSpentLogging;
  }

  public addLoggingTime(loggingTime: number) {
    this._timeSpentLogging += loggingTime;
  }

  public configure(config: LoggerConfig): void {
    const start = getRelativeTimeSinceNavigationStart();
    if (config.uploadCadenceInMs) {
      this._uploadCadenceInMs = config.uploadCadenceInMs;
    }

    if (config.uploadTriggerSize) {
      this._uploadTriggerSize = config.uploadTriggerSize;
    }

    if (config.sessionId) {
      this._userSessionId = config.sessionId;
    }

    if (config.remoteUlsEndpoint) {
      if (!config.sessionId && this._remoteUrlEndpoint) {
        throw new Error("Remote ULS endpoint is already set");
      }

      this._remoteUrlEndpoint = this._transformRemoteUrlEndpoint(
        config.remoteUlsEndpoint
      );
      this._timestampBase = getAbsoluteTimeSinceEpoch();

      // kick off the Uls uploads with scheduleNextUplaod and async set to true
      this._triggerUploadAsync();
    }
    this._timeSpentLogging += getRelativeTimeSinceNavigationStart() - start;
  }

  public handleTrace(loggingArgs: LoggingArgs): void {
    const start = getRelativeTimeSinceNavigationStart();
    // trim the message if it's super long.
    if (loggingArgs.message.length > messageMaxChars) {
      loggingArgs.message =
        loggingArgs.message.substring(
          0,
          messageMaxChars - trimmedMessage.length
        ) + trimmedMessage;
    }

    // push to cache.
    this._logCache.push(loggingArgs);

    // upload if cache is full or the log is an Error-level statement.
    if (
      this._logCache.length > this._uploadTriggerSize ||
      loggingArgs.level === TraceLevel.Error
    ) {
      this._triggerUploadAsync();
    }
    this._timeSpentLogging += getRelativeTimeSinceNavigationStart() - start;
  }

  public flushOnClose = (): void => {
    this._triggerUpload(/* scheduleNextUpload */ false, /* async*/ false);
  };

  public flush = (): void => {
    this._triggerUploadAsync();
  };

  public flushAndSetNewEndpoint = (
    remoteUrlEndpoint: string,
    onComplete?: () => void
  ): void => {
    const transformedRemoteUrlEndpoint =
      this._transformRemoteUrlEndpoint(remoteUrlEndpoint);

    if (
      transformedRemoteUrlEndpoint.localeCompare(
        this._remoteUrlEndpoint ?? "",
        undefined,
        { sensitivity: "accent" }
      )
    ) {
      this._onCompleteUploadCallbacks.push(() => {
        this._remoteUrlEndpoint = transformedRemoteUrlEndpoint;

        if (onComplete) {
          onComplete();
        }
      });

      this._triggerUpload(/* scheduleNextUpload */ true, /* async */ true);
    }
  };

  public updateEndpointAndFlush = (remoteUrlEndpoint: string): void => {
    this._remoteUrlEndpoint =
      this._transformRemoteUrlEndpoint(remoteUrlEndpoint);
    this._triggerUpload(/* scheduleNextUpload */ true, /* async */ true);
  };

  public getUserSessionId(): string {
    return this._userSessionId;
  }

  private _transformRemoteUrlEndpoint = (remoteUrlEndpoint: string): string => {
    // New query param 'officeserverversion' is needed for the ApplicationVersion field in the logs written by the Telemetry Role machines
    return (
      remoteUrlEndpoint +
      "?usid=" +
      this._userSessionId +
      "&officeserverversion=" +
      getBuildNumber()
    );
  };

  private _triggerUploadAsync = (): void => {
    window.setTimeout(
      () =>
        this._triggerUpload(/* scheduleNextUpload */ true, /* async */ true),
      0
    );
  };

  private _triggerUpload = (
    scheduleNextUpload: boolean,
    async: boolean
  ): void => {
    const start = getRelativeTimeSinceNavigationStart();
    window.clearTimeout(this._timeoutId);

    if (!this._remoteUrlEndpoint) {
      return;
    }

    if (this._logCache.length > 0) {
      // increment upload Id
      this._uploadId += 1;

      // get a batch of logs
      const batch = this._logCache.splice(
        0,
        scheduleNextUpload ? this._uploadTriggerSize : this._logCache.length
      );

      // and upload it after serializing into wire format
      const logsPayload = formatForUpload(
        batch,
        this._userSessionId,
        this._uploadId,
        this._timestampBase
      );
      uploadLogs(logsPayload, this._remoteUrlEndpoint, async);

      // reset cache
      this._timestampBase = getAbsoluteTimeSinceEpoch();
    }

    // queue the onComplete callbacks if the cache is empty now
    if (
      this._logCache.length === 0 &&
      this._onCompleteUploadCallbacks.length > 0
    ) {
      this._onCompleteUploadCallbacks.forEach((callback) => {
        const isRequestIdleCallbackAvailable = "requestIdleCallback" in window;
        if (isRequestIdleCallbackAvailable) {
          window.requestIdleCallback(callback);
        } else {
          window.setTimeout(callback, 0);
        }
      });
      this._onCompleteUploadCallbacks = [];
    }

    // set the next execution if requested
    if (scheduleNextUpload) {
      // if we still have many logs to upload, we need to do it more frequently, that is, every 1 second
      // we also upload more frequently if someone has requested an onComplete callback, to make sure the cache actually clears
      this._timeoutId = window.setTimeout(
        () => {
          this._triggerUploadAsync();
        },
        this._logCache.length > this._uploadTriggerSize ||
          this._onCompleteUploadCallbacks.length > 0
          ? fastUploadCadenceInMs
          : this._uploadCadenceInMs
      );
    }

    this._timeSpentLogging += getRelativeTimeSinceNavigationStart() - start;
  };
}
